/**
 * What a ship can carry.
 *
 * Capacity is the sum of its vaults, so hold space is bought with surface area
 * that could have been guns or engines. That is the trade the design doc puts
 * at the centre of ship building, and making capacity a *component* rather than
 * a stat on the hull is what keeps it a real decision.
 *
 * Weight is the only currency here. Lines weigh a hundredth of their length, so
 * a hold is measured in the thing the game is actually made of — twenty weight
 * is two thousand units of line, or a mixed hold of ore and salvage.
 *
 * Linelits are weightless on purpose. They are the money, and money you cannot
 * carry home is a tax on succeeding; the risk on carried linelits is that they
 * are lost with the ship, not that they fill the hold.
 */
import { COMPONENT_SPECS } from '../components.js';
import { DEFAULT_LINE_COLOR, LINE_HEALTH } from './upgrades.js';
/**
 * A line's weight, from its length.
 *
 * The starting ratio, and deliberately a plain one: a hundred units of line is
 * one weight, so a player can do the arithmetic without a calculator.
 */
export const WEIGHT_PER_LINE_UNIT = 1 / 100;
export function lineWeight(length) {
    return length * WEIGHT_PER_LINE_UNIT;
}
/** Total hold space, in weight. */
export function cargoCapacity(components) {
    return components.reduce((total, component) => total + (COMPONENT_SPECS[component.type].cargoCapacity ?? 0), 0);
}
/**
 * What a joint weighs.
 *
 * Not in the weight table this came from. Sized as a tenth — a joint is a
 * fitting rather than a part, and a hundred of them weighing as much as two
 * small thrusters feels about right — and flagged so it stays a decision
 * waiting to be made rather than a number that quietly appeared.
 */
export const JOINT_WEIGHT = 0.1;
/** What one of these weighs. */
export function unitWeight(item) {
    switch (item.kind) {
        case 'line':
            return lineWeight(item.length ?? 0);
        case 'component':
            return item.type ? COMPONENT_SPECS[item.type].weight : 0;
        case 'joint':
            return JOINT_WEIGHT;
        default:
            return item.weight ?? 0;
    }
}
export function itemWeight(item) {
    return unitWeight(item) * item.count;
}
export function cargoWeight(items) {
    return items.reduce((total, item) => total + itemWeight(item), 0);
}
/**
 * Where a hold stands.
 *
 * `free` can go negative, and that is a real state rather than a bug: a vault
 * destroyed in flight takes its capacity with it while its contents are still
 * aboard. Flight decides what that costs — spilling cargo, refusing to launch —
 * but the arithmetic has to be able to express it.
 */
export function cargoState(components, items) {
    const capacity = cargoCapacity(components);
    const used = cargoWeight(items);
    return { capacity, used, free: capacity - used, overloaded: used > capacity };
}
/** How many more of an item would fit. Zero when it is already full. */
export function roomFor(state, each) {
    if (each <= 0)
        return Infinity;
    return Math.max(0, Math.floor(state.free / each));
}
// ─── Putting things in and taking them out ───────────────────────────────────
/**
 * What an entry of this kind is filed under.
 *
 * Two lines stack only if they are the same length *and* the same grade and
 * colour — a sintered wall and a standard one are not interchangeable, and a
 * hold that pretended otherwise would hand you the wrong one.
 */
export function stackKey(item) {
    switch (item.kind) {
        case 'line':
            return `line:${item.length}:${item.strength}:${item.color}`;
        case 'component':
            return `component:${item.type}`;
        case 'joint':
            return 'joint';
        case 'trinket':
            // The id already carries the price paid, which is what makes two lots
            // bought at different prices two lots. Merging a batch bought at 45 with
            // one bought at 60 would leave the hold unable to say what either cost.
            return `trinket:${item.id}`;
        default:
            return `resource:${item.label}`;
    }
}
/**
 * Put something aboard, if it fits.
 *
 * Returns null rather than a boolean-and-a-mutation, so a caller cannot half
 * complete a purchase by ignoring a flag. Refusing is the normal case — a hold
 * that silently grew would make vaults pointless.
 */
export function stow(items, incoming, capacity) {
    const adding = unitWeight(incoming) * incoming.count;
    if (cargoWeight(items) + adding > capacity + 1e-9)
        return null;
    const key = stackKey(incoming);
    const existing = items.findIndex((item) => stackKey(item) === key);
    if (existing < 0)
        return [...items, { ...incoming }];
    const next = [...items];
    next[existing] = { ...next[existing], count: next[existing].count + incoming.count };
    return next;
}
/** Take some out. Empty stacks are dropped rather than left at zero. */
export function take(items, id, count = 1) {
    return items
        .map((item) => (item.id === id ? { ...item, count: item.count - count } : item))
        .filter((item) => item.count > 0);
}
/**
 * The hold as the ship editor wants it: one entry per physical line.
 *
 * Stacks are expanded because `fitLine` picks the tightest piece that will do,
 * and it cannot make that choice against a count.
 */
export function heldLines(items) {
    const lines = [];
    for (const item of items) {
        if (item.kind !== 'line')
            continue;
        for (let i = 0; i < item.count; i++) {
            lines.push({
                id: `${item.id}#${i}`,
                length: item.length ?? 0,
                health: LINE_HEALTH,
                strength: item.strength ?? 1,
                color: item.color ?? DEFAULT_LINE_COLOR,
            });
        }
    }
    return lines;
}
export function heldJoints(items) {
    return items.reduce((total, item) => total + (item.kind === 'joint' ? item.count : 0), 0);
}
export function heldComponents(items) {
    const counts = {};
    for (const item of items) {
        if (item.kind !== 'component' || !item.type)
            continue;
        counts[item.type] = (counts[item.type] ?? 0) + item.count;
    }
    return counts;
}
/** Take one part of this type out of the hold, wherever it is stacked. */
export function takeComponent(items, type) {
    const found = items.find((item) => item.kind === 'component' && item.type === type);
    return found ? take(items, found.id) : null;
}
/** And a joint. */
export function takeJoint(items, count = 1) {
    const found = items.find((item) => item.kind === 'joint');
    if (!found || found.count < count)
        return null;
    return take(items, found.id, count);
}
/**
 * A loose component, as it sits in a hold.
 *
 * One definition, because two places now put parts into cargo — a trader
 * selling one, and a hull throwing one out through a hole — and a stack only
 * merges with another of the same id. Two spellings of the same engine would sit
 * in the hold as two separate stacks of one.
 */
export function partCargo(type) {
    return {
        id: `component-${type}`,
        kind: 'component',
        label: COMPONENT_SPECS[type].name,
        type,
        count: 1,
    };
}
//# sourceMappingURL=cargo.js.map